/**
 * Generates the HTML content for the PDF loading screen
 * @param {string} title - The title to display
 * @param {string} message - The loading message to display
 * @param {string} language - The language code (e.g., 'de', 'en')
 * @returns {string} Complete HTML string for the loading page
 */
export const getPdfLoadingHTML = ({ title, message, language = 'de' }) => `
  <!DOCTYPE html>
  <html lang="${language}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: #4461D4;
          color: white;
        }
        .container {
          text-align: center;
          padding: 2rem;
        }
        .spinner {
          width: 60px;
          height: 60px;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 2rem;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        h1 {
          font-size: 1.75rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }
        p {
          font-size: 1.125rem;
          opacity: 0.95;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="spinner"></div>
        <h1>${title}</h1>
        <p>${message}</p>
      </div>
    </body>
  </html>
`

/**
 * Opens a new window and writes the PDF loading HTML
 * @param {Object} params
 * @param {Function} params.t - Translation function
 * @param {string} params.language - Language code
 * @returns {Window|null} The opened window reference
 */
export const openPdfLoadingWindow = ({ t, language }) => {
  const pdfWindow = window.open('', '_blank')
  if (!pdfWindow) return null

  const loadingHTML = getPdfLoadingHTML({
    title: t('pdf_preparation_title'),
    message: t('pdf_preparation_message'),
    language
  })

  pdfWindow.document.write(loadingHTML)
  pdfWindow.document.close()

  return pdfWindow
}
